import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChefHat, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ContactUs = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }, 2000);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['Plot No. 74, KanakVrindvan Township', 'Jaipur, Rajasthan, India'],
      color: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91 8107436861', 'Mon-Sun: 9:00 AM - 11:00 PM'],
      color: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['orders@btechbawarchi.com', 'support@btechbawarchi.com', 'feedback@btechbawarchi.com'],
      color: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Monday - Sunday: 9:00 AM - 11:00 PM', 'Kitchen Hours: 10:00 AM - 10:30 PM', 'Delivery: 11:00 AM - 11:00 PM'],
      color: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
    }
  ];

  const faqs = [
    {
      question: 'What are your delivery areas?',
      answer: 'We deliver to all areas within Jaipur including Vaishali Nagar, Civil Lines, C-Scheme, and surrounding areas. Delivery charges may vary based on distance.'
    },
    {
      question: 'How long does delivery take?',
      answer: 'Our average delivery time is 25-35 minutes. However, during peak hours (12:00 PM - 2:00 PM and 7:00 PM - 9:00 PM), delivery may take up to 45 minutes.'
    },
    {
      question: 'Do you offer bulk orders for parties?',
      answer: 'Yes! We specialize in party orders and bulk catering. Please contact us at least 24 hours in advance for orders above ₹2000. We offer special party packages and custom menus.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major payment methods including cash on delivery, UPI, credit/debit cards, net banking, and digital wallets like Paytm, PhonePe, and Google Pay.'
    },
    {
      question: 'Do you have vegetarian and vegan options?',
      answer: 'Absolutely! We have an extensive vegetarian menu and can prepare vegan dishes upon request. All our vegetarian items are clearly marked with a green VEG symbol.'
    },
    {
      question: 'Can I modify my order after placing it?',
      answer: 'You can modify or cancel your order within 5 minutes of placing it by calling our customer service. After that, changes may not be possible as we start preparing your food immediately.'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
      color: '#333',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>B.Tech Bawarchi</h1>
          </div>
          <nav style={{ display: 'flex', gap: '24px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Home</Link>
            <Link to="/about" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>About</Link>
            <Link to="/contact" style={{ color: '#f97316', textDecoration: 'none', fontWeight: '600' }}>Contact</Link>
            <Link to="/menu" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Menu</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        padding: '60px 16px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(251, 115, 22, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          transition: 'all 1s ease',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1f2937',
            margin: '0 0 16px 0',
            letterSpacing: '-0.025em',
            lineHeight: '1.1'
          }}>
            Get in{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #dc2626 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Touch
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 32px 0'
          }}>
            Have questions, feedback, or need assistance? We're here to help! 
            Reach out to us through any of the channels below.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section style={{
        padding: '80px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 0.2s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Contact Information
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Multiple ways to reach us for orders, support, or feedback.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '32px'
          }}>
            {contactInfo.map((info, index) => (
              <div
                key={index}
                style={{
                  padding: '32px',
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: `all 1s ease ${0.4 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  background: info.color,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                }}>
                  <info.icon size={28} color="white" />
                </div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 16px 0'
                }}>
                  {info.title}
                </h4>
                {info.details.map((detail, idx) => (
                  <p key={idx} style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    margin: '0 0 8px 0',
                    lineHeight: '1.5'
                  }}>
                    {detail}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section style={{
        padding: '80px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 0.6s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Send us a Message
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Have a specific question or feedback? Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>
          
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '48px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            transition: 'all 1s ease 0.8s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            {submitStatus === 'success' && (
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <CheckCircle size={20} color="#22c55e" />
                <p style={{
                  color: '#166534',
                  margin: 0,
                  fontWeight: '600'
                }}>
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                gap: '24px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                gap: '24px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px',
                      transition: 'border-color 0.3s ease',
                      boxSizing: 'border-box',
                      background: 'white'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  >
                    <option value="">Select a subject</option>
                    <option value="order-inquiry">Order Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="complaint">Complaint</option>
                    <option value="partnership">Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    transition: 'border-color 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f97316'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="Tell us how we can help you..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(251, 115, 22, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{
        padding: '80px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '60px',
            transition: 'all 1s ease 1s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Frequently Asked Questions
            </h3>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Quick answers to common questions about our services and policies.
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gap: '24px'
          }}>
            {faqs.map((faq, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  transition: `all 1s ease ${1.2 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
              >
                <div style={{
                  padding: '24px'
                }}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: '0 0 12px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <MessageSquare size={20} color="#f97316" />
                    {faq.question}
                  </h4>
                  <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    margin: 0,
                    paddingLeft: '32px'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#1f2937',
        color: 'white',
        padding: '48px 16px 24px 16px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '8px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0
            }}>B.Tech Bawarchi</h4>
          </div>
          <p style={{
            color: '#9ca3af',
            marginBottom: '24px',
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            Serving authentic Indian cuisine with love and tradition since 2019.
          </p>
          <div style={{
            borderTop: '1px solid #374151',
            paddingTop: '16px',
            color: '#9ca3af',
            fontSize: '12px'
          }}>
            <p style={{ margin: 0 }}>&copy; 2024 B.Tech Bawarchi. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ContactUs;
